import { Injectable, signal, type WritableSignal } from '@angular/core';
import type { WizardStartTarget } from './wizard-host.types';

/**
 * Host ↔ wizard DI channel for page-hosted wizards (e.g. the onboarding status page).
 *
 * The host owns the domain knowledge — which steps or documents a reviewer flagged, whether the
 * current edit session changed anything — and publishes it here. The generic `wizard` and
 * `required-documents-checklist` field types read it via optional `inject()`; `wizard-stepper`
 * has no dependency on this service at all — it renders the notices the wizard passes it through
 * a `[notices]` input. None of them carry module-specific entry keys or status slugs of their own.
 *
 * NOT `providedIn: 'root'`: a host provides this in its OWN component `providers` (see
 * `onboarding-status.component.ts`), the same way `WizardFooterSlot` is scoped to
 * `CreateFormModalComponent`. A wizard used in a normal module form or entry page injects this
 * optionally and gets `null` — no notices, no edit-session gating, no page-completion callback —
 * exactly as it did when these keys were simply absent from `formState`.
 *
 * One producer, one direction: the host writes each signal below directly (`.set(...)`); every
 * field type (`wizard.component.ts`, `required-documents-checklist.component.ts`) only ever reads.
 */
@Injectable()
export class WizardHostContext {
  /**
   * Per wizard page target (`page:<key>`): the notice the step shows (stepper dot, tooltip and the
   * one-line text under the step title). Empty entry / absent key → no marking.
   *
   * Rendered VERBATIM — the wizard adds no lead-in, no punctuation and no framing of its own, on
   * screen or in the step's aria-label. A host that wants "Aanpassing gevraagd: …" in front of the
   * reviewer's words writes it here; the generic field type has no idea what the notice is about.
   */
  readonly stepNotices: WritableSignal<Readonly<Record<string, string>>> = signal({});

  /**
   * Notice for the step that hosts the documents checklist, when documents were flagged — the
   * document lines are about individual rows, so a page notice on that same step is shown first and
   * this one follows it after a `; `. Verbatim, like {@link stepNotices}.
   */
  readonly documentsNotice: WritableSignal<string | null> = signal(null);

  /** Per document-record id: the notice the checklist row shows (the reviewer's flag reason). */
  readonly documentNotices: WritableSignal<Readonly<Record<string, string>>> = signal({});

  /** True while the host runs an edit session on an already submitted entry. */
  readonly editSession: WritableSignal<boolean> = signal(false);

  /**
   * The host's single definition of "did this session change anything" — a saved-value baseline
   * diff plus edits made outside the wizard (e.g. a document replaced on the status page). The
   * wizard falls back to `!editSession()` gating alone when there is no callback at all (an
   * unhosted wizard never reaches the code that reads this).
   *
   * Opus-verified — regression fix: a PULL callback, deliberately NOT a signal. It was
   * briefly a `WritableSignal<boolean>` fed by `enterEditMode`/`exitEditMode` and two `form.events`
   * bridges (see `onboarding-status.component.ts` git history), which by construction only reacts
   * to writes that go through Angular's own event pipeline. A dozen field types write their own
   * control with `{ emitEvent: false }` — the autocomplete's free-text commit, address,
   * geolocation, inline-entries (see `wizard.component.ts`'s note on the dozen field types, ~line
   * 700) — and none of those ever reached the signal, so the last step kept offering "Terug naar
   * status" (and hiding submit) after exactly the edits a carrier is most likely to make. A PULL
   * callback re-evaluated on every read has no such blind spot: it asks the host's own
   * `DetailsService.hasUnsavedChanges` fresh each time, which walks the live form value regardless
   * of how it got there.
   *
   * Assigned ONCE by the host (like {@link onExit}/{@link onPageComplete}), never re-assigned per
   * publish. The wizard reads it with `context.hasUnsavedChanges?.() ?? false`, wrapped in
   * `untracked` where the read happens inside a reactive consumer (a template binding or a
   * `computed()`) — the callback reads the HOST's own signals (`DetailsService.form()`, the status
   * page's own form), and those must not become a dependency of the WIZARD's reactive graph.
   */
  hasUnsavedChanges?: () => boolean;

  /** Host callback behind the wizard's "Terug naar status" exit. */
  onExit?: () => void;

  /** Host callback invoked with the completion action that ran. */
  onPageComplete?: (action: unknown) => void;

  /**
   * One-shot start target (`page:<key>`), consumed ONCE by the wizard when it mounts — see
   * {@link consumeStartTarget}. Plain field, not a signal: nothing should derive from it
   * reactively, and the host writes it synchronously right before the wizard is created (see
   * `enterEditMode()`), which a signal write cannot improve on and an effect-driven write would
   * actively endanger (a re-run before the wizard's `ngOnInit` reads it would wipe the jump).
   */
  #startTarget: WizardStartTarget | null = null;

  /** Publish the page the wizard should open on next time it mounts. */
  setStartTarget(target: WizardStartTarget | null): void {
    this.#startTarget = target;
  }

  /**
   * Read and clear the pending start target atomically. Consumed ONCE by the wizard's own
   * `ngOnInit` — a later read (e.g. a re-render, or a second wizard instance) must not repeat the
   * jump, which would strand a user who has since navigated away from that step on purpose.
   */
  consumeStartTarget(): WizardStartTarget | null {
    const target = this.#startTarget;
    this.#startTarget = null;
    return target;
  }
}
